import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { fmtPeriod } from "@/lib/period";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));

// Iter261: Lohn für einen offenen Lohnlauf anpassen (Bonus, Zulage, Abzug, Stunden/Ansatz/Grundlohn)
export function PayrollAdjustDialog({ run, companyId, employee, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const b = run?.breakdown || {};
  const hourly = b.employment_type === "hourly";

  useEffect(() => {
    if (!run) return setF(null);
    const a = run.adjustments || {};
    setF({
      bonus_amount: b.bonus_amount ? String(b.bonus_amount) : "",
      allowance_tax_free: b.allowance_tax_free ? String(b.allowance_tax_free) : "",
      manual_deduction: b.manual_deduction ? String(b.manual_deduction) : "",
      manual_deduction_label: b.manual_deduction_label || "",
      hours_worked: hourly && b.hours_worked ? String(b.hours_worked) : "",
      hourly_rate: hourly && b.hourly_rate ? String(b.hourly_rate) : "",
      base_salary: !hourly && b.monthly_salary ? String(b.monthly_salary) : "",
      note: a.note || "",
      skip_receivable_offset: !!a.skip_receivable_offset,
    });
  }, [run?._id]);

  if (!run || !f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        bonus_amount: num(f.bonus_amount) ?? 0,
        allowance_tax_free: num(f.allowance_tax_free) ?? 0,
        manual_deduction: num(f.manual_deduction) ?? 0,
        manual_deduction_label: f.manual_deduction_label || null,
        note: f.note || null,
        skip_receivable_offset: !!f.skip_receivable_offset,
      };
      if (hourly) { payload.hours_worked = num(f.hours_worked); payload.hourly_rate = num(f.hourly_rate); }
      else payload.base_salary = num(f.base_salary);
      const r = await apiClient.post(`/companies/${companyId}/payroll-runs/${run._id}/adjust`, payload);
      toast.success(`Lohnlauf ${fmtPeriod(run.period)} neu berechnet – Brutto CHF ${r.data.gross_salary.toFixed(2)}, Netto CHF ${r.data.net_salary.toFixed(2)}`);
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anpassung fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="payroll-adjust-dialog">
        <DialogHeader>
          <DialogTitle>Lohn anpassen – {employee ? `${employee.first_name} ${employee.last_name}, ` : ""}{fmtPeriod(run.period)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            {hourly ? (
              <>
                <div className="space-y-1">
                  <Label>Stunden diesen Monat</Label>
                  <Input type="number" step="0.25" value={f.hours_worked} onChange={set("hours_worked")} data-testid="adjust-hours-input" />
                </div>
                <div className="space-y-1">
                  <Label>Stundenansatz (CHF/h)</Label>
                  <Input type="number" step="0.05" value={f.hourly_rate} onChange={set("hourly_rate")} data-testid="adjust-hourly-rate-input" />
                </div>
              </>
            ) : (
              <div className="space-y-1 col-span-2">
                <Label>Grundlohn diesen Monat (Brutto)</Label>
                <Input type="number" step="0.05" value={f.base_salary} onChange={set("base_salary")} data-testid="adjust-base-salary-input" />
                <p className="text-[11px] text-slate-500">Nur für diesen Lohnlauf – der Mitarbeiterstamm bleibt unverändert.</p>
              </div>
            )}
            <div className="space-y-1">
              <Label>Bonus / Prämie (AHV-pflichtig)</Label>
              <Input type="number" step="0.05" value={f.bonus_amount} onChange={set("bonus_amount")} data-testid="adjust-bonus-input" />
            </div>
            <div className="space-y-1">
              <Label>Spesen / Zulage (AHV-frei)</Label>
              <Input type="number" step="0.05" value={f.allowance_tax_free} onChange={set("allowance_tax_free")} data-testid="adjust-allowance-input" />
            </div>
            <div className="space-y-1">
              <Label>Abzug vom Netto (z.B. Vorschuss)</Label>
              <Input type="number" step="0.05" value={f.manual_deduction} onChange={set("manual_deduction")} data-testid="adjust-deduction-input" />
            </div>
            <div className="space-y-1">
              <Label>Bezeichnung Abzug</Label>
              <Input value={f.manual_deduction_label} placeholder="Lohnvorschuss" onChange={set("manual_deduction_label")} data-testid="adjust-deduction-label-input" />
            </div>
          </div>
          {(run.breakdown?.receivable_offset || f.skip_receivable_offset) && (
            <label className="flex items-start gap-2 rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-900 cursor-pointer" data-testid="adjust-skip-offset-label">
              <input type="checkbox" className="mt-0.5" checked={f.skip_receivable_offset} onChange={(e) => setF({ ...f, skip_receivable_offset: e.target.checked })} data-testid="adjust-skip-offset-checkbox" />
              <span>
                <b>Rückforderung nicht verrechnen</b>
                {run.breakdown?.receivable_offset && (
                  <> – aktuell werden CHF {Number(run.breakdown.receivable_offset.amount).toFixed(2)} aus Lohnkorrektur {(run.breakdown.receivable_offset.periods || []).map(fmtPeriod).join(", ")} vom Netto abgezogen.</>
                )}
              </span>
            </label>
          )}
          <div className="space-y-1">
            <Label>Bemerkung (intern)</Label>
            <Input value={f.note} onChange={set("note")} placeholder="z.B. Bonus Q3 gemäss Zielvereinbarung" data-testid="adjust-note-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Abbrechen</Button>
            <Button type="button" size="sm" className="bg-slate-900 text-slate-50" disabled={saving} onClick={save} data-testid="adjust-save-button">
              {saving ? "Berechnet …" : "Neu berechnen & speichern"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
